import { Badge, Error, Form, Icon, Input, InputGroup, Select, ValidationStates } from '@opengov/component-library/dist';
import { IconSVGs } from '@opengov/component-library/dist/Icon/IconSVGs';
import { BasicColumnTypes } from '@opengov/ogql-types';
import { EReportTargetComparisonOperators } from '@opengov/reporting-types';
import React from 'react';

import { IReportLocale } from '../../../locale';

import styles from './SetTargetModal.scss';

export interface ITargetConditionInputProps {
  fieldName: string;
  showColumnType: BasicColumnTypes;
  reportLocaleText: IReportLocale;
  operator: EReportTargetComparisonOperators;
  value: number;
  statusText: string;
  statusPlaceholder?: string;
  valueTextValidationErrorMessage?: string;
  valueTextValidationState: ValidationStates;
  statusTextValidationState: ValidationStates;
  isConditionEnabled?: boolean;
  onChangeStatusText(statusText: string): void;
  onChangeValue(value: string): void;
  onChangeOperator(operator: EReportTargetComparisonOperators): void;
}

export type TargetConditionSelectOptions = {
  [key in EReportTargetComparisonOperators]?: { label: string; icon: keyof typeof IconSVGs };
};

export const makeSelectOptions = (reportLocaleText: IReportLocale): TargetConditionSelectOptions => {
  return {
    [EReportTargetComparisonOperators.greaterThan]: {
      label: reportLocaleText.setTargetModal.conditions.greaterThan,
      icon: 'operator-greaterthan'
    },
    [EReportTargetComparisonOperators.greaterThanOrEqualTo]: {
      label: reportLocaleText.setTargetModal.conditions.greaterThanOrEqualTo,
      icon: 'operator-greaterthanorequal'
    },
    [EReportTargetComparisonOperators.lessThan]: {
      label: reportLocaleText.setTargetModal.conditions.lessThan,
      icon: 'operator-lessthan'
    },
    [EReportTargetComparisonOperators.lessThanOrEqualTo]: {
      label: reportLocaleText.setTargetModal.conditions.lessThanOrEqualTo,
      icon: 'operator-lessthanorequal'
    },
    [EReportTargetComparisonOperators.equalTo]: {
      label: reportLocaleText.setTargetModal.conditions.equalTo,
      icon: 'operator-equal'
    },
    [EReportTargetComparisonOperators.notEqualTo]: {
      label: reportLocaleText.setTargetModal.conditions.notEqualTo,
      icon: 'operator-notequal'
    }
  };
};

export const valueIconKey = (columnType: BasicColumnTypes): keyof typeof IconSVGs => {
  switch (columnType) {
    case BasicColumnTypes.Currency:
      return 'datatype-currency';
    case BasicColumnTypes.Percent:
      return 'datatype-percentage';
    case BasicColumnTypes.Numeric:
      return 'datatype-numeric';
    default:
      return 'datatype-numeric';
  }
};

export const TargetConditionInput: React.FC<ITargetConditionInputProps> = ({
  fieldName,
  reportLocaleText,
  showColumnType,
  operator,
  value,
  statusText,
  statusPlaceholder,
  valueTextValidationErrorMessage,
  valueTextValidationState,
  statusTextValidationState,
  isConditionEnabled = true,
  onChangeStatusText,
  onChangeValue,
  onChangeOperator
}) => {
  const selectOptions = makeSelectOptions(reportLocaleText);
  return (
    <div className={styles.conditionControls}>
      <Form.Item label={reportLocaleText.setTargetModal.condition}>
        <Select
          disabled={!isConditionEnabled}
          placeholder={reportLocaleText.setTargetModal.condition}
          dataTest={`set-target-modal-${fieldName}-select-condition`}
          value={operator}
          onChange={onChangeOperator}
        >
          {Object.keys(selectOptions).map((key: keyof typeof selectOptions) => {
            const config = selectOptions[key];
            const dataTestPrefix = `set-target-modal-${fieldName}-select-condition-option`;
            return (
              <Select.Option
                prefixIcon={
                  <Icon icon={config?.icon ?? 'operator-contains'} dataTest={`${dataTestPrefix}-${key}-icon`} />
                }
                dataTest={`${dataTestPrefix}-${key}`}
                key={key}
                label={config?.label ?? ''}
                value={key}
              />
            );
          })}
        </Select>
      </Form.Item>
      <div className={styles.valueInput}>
        <Form.Item label={reportLocaleText.setTargetModal.value}>
          <InputGroup>
            <InputGroup.Prefix>
              <InputGroup.Icon
                icon={valueIconKey(showColumnType)}
                dataTest={`set-target-modal-${fieldName}-value-prefix`}
              />
            </InputGroup.Prefix>
            <Input.Number
              placeholder={reportLocaleText.setTargetModal.value}
              value={value}
              onChange={onChangeValue}
              dataTest={`set-target-modal-${fieldName}-value`}
              validationState={valueTextValidationState}
            />
          </InputGroup>
          {valueTextValidationState === ValidationStates.Error && valueTextValidationErrorMessage && (
            <Error dataTest={`set-target-modal-${fieldName}-value-error-msg`} className={styles.validationError}>
              {valueTextValidationErrorMessage}
            </Error>
          )}
        </Form.Item>
      </div>
      <div className={styles.statusInput}>
        <Form.Item label={reportLocaleText.setTargetModal.status}>
          <Input.Text
            placeholder={statusPlaceholder || reportLocaleText.setTargetModal.status}
            value={statusText}
            onChange={onChangeStatusText}
            dataTest={`set-target-modal-${fieldName}-status`}
            validationState={statusTextValidationState}
          />
        </Form.Item>
      </div>
      <div className={styles.statusBadge}>
        <Form.Item label={reportLocaleText.setTargetModal.preview}>
          {statusText && (
            <Badge
              text={statusText}
              variant={fieldName === 'second-condition' ? 'warning' : 'positive'}
              maxLength={12}
            />
          )}
        </Form.Item>
      </div>
    </div>
  );
};
